import React, { useState, useEffect } from 'react';
import { authHeaders } from '../authToken';
import '../admin.css';

/*
 * Admin dashboard — a separate screen (route /admin), reached from the hamburger
 * menu and shown only to admin accounts. Overview metrics for now; teacher
 * approvals / user management / platform links slot in as future sections.
 */

function Stat({ label, value, sub }) {
  return (
    <div className="ad-card">
      <div className="ad-card-value">{value}</div>
      <div className="ad-card-label">{label}</div>
      {sub && <div className="ad-card-sub">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/admin/overview', { headers: authHeaders() });
        if (!r.ok) throw new Error('Could not load the admin overview.');
        setData(await r.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="ad-root">
      <header className="ad-topbar">
        <button className="ad-back" onClick={onBack} aria-label="Back to app">‹ Back</button>
        <h1 className="ad-title">Admin</h1>
      </header>

      {error && <div className="ad-error" role="alert">{error}</div>}

      <div className="ad-body">
        {loading || !data ? (
          <p className="ad-muted">Loading…</p>
        ) : (
          <>
            <section>
              <h2 className="ad-h2">Overview</h2>
              <div className="ad-cards">
                <Stat label="Total users" value={data.accounts.total} />
                <Stat label="Students" value={data.accounts.students} />
                <Stat label="Teachers" value={data.accounts.teachers} />
                <Stat label="Schools" value={data.schools} />
                <Stat label="Classes" value={data.classes} />
              </div>
            </section>

            <section>
              <h2 className="ad-h2">Last {data.days} days</h2>
              <div className="ad-cards">
                <Stat label="Logins" value={data.logins.total} sub={`${data.logins.distinct_users} distinct users`} />
                <Stat label="Active students" value={data.active_students} />
                <Stat label="New signups" value={data.signups} />
              </div>

              <div className="ad-two">
                <div className="ad-panel">
                  <h3 className="ad-h3">Logins by role</h3>
                  {Object.keys(data.logins_by_role).length === 0 ? (
                    <p className="ad-muted">No logins recorded yet.</p>
                  ) : (
                    <ul className="ad-list">
                      {Object.entries(data.logins_by_role).map(([role, n]) => (
                        <li key={role}><span>{role}</span><span className="ad-num">{n}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ad-panel">
                  <h3 className="ad-h3">Logins by school</h3>
                  {data.logins_by_school.length === 0 ? (
                    <p className="ad-muted">No logins recorded yet.</p>
                  ) : (
                    <ul className="ad-list">
                      {data.logins_by_school.map(s => (
                        <li key={s.school}><span>{s.school}</span><span className="ad-num">{s.logins}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <p className="ad-note">Login figures count from 21 Aug 2026, when tracking was added.</p>
            </section>

            <section>
              <h2 className="ad-h2">Tools</h2>
              <p className="ad-muted">Coming next: InThinking teacher approvals, user management, and platform links.</p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
